import { useState, useMemo, useEffect, useRef } from "react";
import tableArrowDown from "../../assets/images/icons/table/table-arrow-down.svg";
import tableArrowUp from "../../assets/images/icons/table/table-arrow-up.svg";
import {
  LabelText,
  LittleSubHeadline,
  LittleText,
  MediumText,
  NotificationText,
} from "../typography";
import styled, { css } from "styled-components";
import { COLORS } from "../../assets/utils/enums/colors";
import { boxShadow, statusBtn } from "../components-style";
import tableEditIcon from "../../assets/images/icons/table/table-edit-icon.svg";
import tableFilterIcon from "../../assets/images/icons/table/table-filter-icon.svg";
import { Button, CopyButton } from "./button";
import Checkbox from "./checkbox";
import arrowLeft from "../../assets/images/icons/arrow-left-chevron.svg";
import arrowRight from "../../assets/images/icons/arrow-right-chevron.svg";
import plusIcon from "../../assets/images/icons/plus-white-icon.svg";
import {
  prioritiesObj,
  PriorityEnum,
  StageEnum,
  stagesObj,
  TypeClientEnum,
  typeClientObj,
} from "../../assets/utils/enums/project";
import Dropdown from "./dropdown";
import { useTablePageItemsOptions } from "../../assets/utils/hooks/useTablePageItemsOptions";
import Input, { InputStyle } from "./inputs";
import {
  DropdownTypeEnum,
  tableColumnType,
  TableTypeEnum,
} from "../../assets/utils/enums/table";
import doneWhiteIcon from "../../assets/images/icons/done-icon-white.svg";
import { useLocation, useNavigate } from "react-router-dom";
import pluralizeElements from "../../assets/utils/pluralize-elements";
import { formatDate, getChangePercentTableStatisticByMetric } from "../../assets/utils/date-formatter";
import deleteIcon from "../../assets/images/icons/delete-red-icon.svg";
import ModalWindow from "./modal-window";
import { ActionsEnum } from "../../assets/utils/enums/settings";
import { linkText } from "./error-text";
import { FinanceTypeTable } from "../../assets/utils/enums/finance";
import { useStatisticPeriod } from "../../assets/store/useStatisticPeriod";
import { useOutsideClick } from "../../assets/utils/hooks/useOutsideClick";
import dotsIcon from '../../assets/images/icons/three-dots-column.svg'
import penIcon from '../../assets/images/icons/edit-icon.svg';
import deleteDarkIcon from '../../assets/images/icons/delete-icon.svg';
import toast from "react-hot-toast";
import CustomToast from "./custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";

interface Column {
  key: string;
  label: string;
  visible: boolean;
}
interface DataTableProps {
  table: { name: string; columns: Column[] };
  data: Record<string, any>[];
  isPage?: TableTypeEnum | null;
  itemsCount?: number;
  deleteArrFunc?: (queryArr: number[]) => void;
  isFinanceTitle?: FinanceTypeTable;

}

export function getEnumKeyByName(name: string): TableTypeEnum | undefined {
  return (Object.keys(tableColumnType) as TableTypeEnum[]).find(
    (key) => tableColumnType[key].name === name
  );
}

export default function DataTable({
  table,
  data = [],
  isPage = null,
  itemsCount = 5,
  deleteArrFunc,
  isFinanceTitle,
}: DataTableProps) {
  const storedColumns = localStorage.getItem(
    `${getEnumKeyByName(table.name)}-table-columns`
  );
  const [visibleColumns, setVisibleColumns] = useState<Column[]>(
    storedColumns ? JSON.parse(storedColumns) : table.columns
  );

  const [filter, setFilter] = useState("");
  const [page, setPage] = useState("1");
  const [pageSize, setPageSize] = useState(itemsCount);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const pageSizeOptions = useTablePageItemsOptions(data.length);
  const [isEditTable, setIsEditTable] = useState<boolean>(false);
  const [isFilterTable, setIsFilterTable] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { period, setPeriod } = useStatisticPeriod();

  useEffect(() => {
    // Якщо itemsCount переданий вручну → використовуємо саме його
    if (itemsCount) {
      setPageSize(Math.min(itemsCount, data.length));
      return;
    }

    // Інакше: якщо даних менше базового 5 – показуємо всі
    setPageSize(Math.min(5, data.length));
  }, [data.length, itemsCount]);

  useEffect(() => setSelectedRows(new Set()), [page]);

  const toggleRow = (index: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [isActiveClickedRow, setIsActiveClickedRow] = useState<boolean>(false);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "", direction: "desc" });

  const actionMenuRef = useRef<HTMLDivElement>(null);

  useOutsideClick(actionMenuRef, () => setIsActiveClickedRow(false), isActiveClickedRow);


  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "desc" };
    });
  };

  const filteredData = useMemo(() => {
    setPage("1");
    return data.filter((row) =>
      Object.values(row).join(" ").toLowerCase().includes(filter.toLowerCase())
    );
  }, [data, filter]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice(
    (Number.parseInt(page) - 1) * pageSize,
    Number.parseInt(page) * pageSize
  );

  const toggleColumn = (key: string) => {
    setVisibleColumns((cols) => {
      const updated = cols.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      );
      localStorage.setItem(
        `${getEnumKeyByName(table.name)}-table-columns`,
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  return (
    <TableBlock>
      <div
        className="w-100 d-flex justify-content-between align-items-center gap-4"
        data-type="panel-block"
      >
        <span className="d-flex align-items-center gap-3">
          <MediumText className="text-nowrap">
            {table.name === "Фінанси" ? isFinanceTitle : table.name}
          </MediumText>
          {(getEnumKeyByName(table.name) === TableTypeEnum.TEAM_USERS || getEnumKeyByName(table.name) === TableTypeEnum.STATISTIC_USER_ITEM)
            ? null
            : !currentPath.includes(getEnumKeyByName(table.name) || "") && (
              <Button
                bgcolor={COLORS["bg-text-color"]}
                txtcolor={COLORS.white}
                brcolor={COLORS["light-bg-color"]}
                brradius="32px !important"
                type="large"
                width="100%"
                className="d-flex align-items-center justify-content-center gap-2 px-3"
                onClick={() => {
                  const tableName = getEnumKeyByName(table.name);
                  const linkBody =
                    tableName === TableTypeEnum.COMPANY_EMPLOYEES ||
                      tableName === TableTypeEnum.STATISTIC_CLIENTS
                      ? TableTypeEnum.CLIENTS
                      : tableName;
                  navigate(
                    `/${linkBody}${isFinanceTitle
                      ? isFinanceTitle === FinanceTypeTable.INCOME
                        ? "/income"
                        : "/expense"
                      : ""
                    }/add`
                  );
                }}
              >
                <LittleText>Додати</LittleText>
                <img src={plusIcon} alt="plus-icon" width={20} height={20} />
              </Button>
            )}
        </span>
        {getEnumKeyByName(table.name) === TableTypeEnum.STATISTIC_USER_ITEM ? <Dropdown
          checkedValue={period}
          setChecked={
            setPeriod as React.Dispatch<React.SetStateAction<string>>
          }
          type={DropdownTypeEnum.PERIODS}
        /> : <span data-type="panel-btns" className="d-flex align-items-center">
          {selectedRows.size > 0 && (
            <>
              {" "}
              <Button
                bgcolor={"transparent"}
                txtcolor={COLORS.blue}
                brcolor={COLORS.blue}
                type="large"
                width="100%"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  setIsEditTable(false);
                }}
              >
                <LittleText>
                  {selectedRows.size} {pluralizeElements(selectedRows.size)}{" "}
                  обрано
                </LittleText>
              </Button>
              {deleteArrFunc && (
                <Button
                  bgcolor={"transparent"}
                  txtcolor={COLORS.warning}
                  brcolor={COLORS.warning}
                  type="large"
                  width="100%"
                  className="d-flex align-items-center justify-content-center gap-2"
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  <img src={deleteIcon} alt="delete-icon" />
                  <LittleText>Видалити</LittleText>
                </Button>
              )}
            </>
          )}
          <ModalWindow
            isOpen={isModalOpen}
            onConfirm={() => {
              deleteArrFunc!([...selectedRows]);
              setTimeout(() => {
                setSelectedRows(new Set());
                setIsModalOpen(false);
              }, 150);
            }}
            onCancel={() => {
              setTimeout(() => {
                setIsModalOpen(false);
              }, 150);
            }}
            action={ActionsEnum.DELETE}
          />
          {getEnumKeyByName(table.name) === TableTypeEnum.TEAM_USERS
            ? null : (!isEditTable ? (
              <Button
                bgcolor={"transparent"}
                txtcolor={COLORS.black1}
                brcolor={COLORS.secondary1}
                type="large"
                width="100%"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  setIsEditTable(true);
                }}
              >
                <img src={tableEditIcon} alt="table-edit-icon" />
                <LittleText>Редагувати таблицю</LittleText>
              </Button>
            ) : (
              <Button
                bgcolor={COLORS.main1}
                txtcolor={COLORS.white}
                brcolor={COLORS.main1}
                type="large"
                width="100%"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  setIsEditTable(false);
                }}
              >
                <img width={20} height={20} src={doneWhiteIcon} alt="done-icon" />
                <LittleText>Готово</LittleText>
              </Button>
            ))}
          {isFilterTable ? (
            <span className="d-flex align-items-center justify-content-between">
              <Input
                attribute="filter"
                label={""}
                errorText={""}
                placeholder={"Введіть ключове слово"}
                value={filter}
                setValue={setFilter}
              />
              <Button
                bgcolor={COLORS.main1}
                txtcolor={COLORS.white}
                brcolor={COLORS.main1}
                type="large"
                width="100%"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  setIsFilterTable(false);
                  setFilter("");
                }}
              >
                <img
                  width={20}
                  height={20}
                  src={doneWhiteIcon}
                  alt="done-icon"
                />
                <LittleText>Готово</LittleText>
              </Button>
            </span>
          ) : (
            <Button
              bgcolor={"transparent"}
              txtcolor={COLORS.black1}
              brcolor={COLORS.secondary1}
              type="large"
              width="100%"
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                setIsFilterTable(true);
              }}
            >
              <img src={tableFilterIcon} alt="table-filter-icon" />
              <LittleText>Пошук</LittleText>
            </Button>
          )}
        </span>}
      </div>
      {/* ➕➖ Керування колонками */}

      {isEditTable && (
        <div className="d-flex flex-wrap justify-content-start border-bottom py-3 row-gap-3">
          {table.columns.slice(1).map((col) =>
            (col.key.toLocaleLowerCase().includes("project") &&
              isPage === TableTypeEnum.PROJECTS) ||
              (col.key.toLocaleLowerCase().includes("selling") &&
                isPage === ("selling" as TableTypeEnum)) ||
              col.key.toLocaleLowerCase().includes("id") ||
              col.key === "projectId" ||
              col.key === "sellingId" ? null : (
              <span
                className="d-flex align-items-end gap-1"
                style={{ flex: "0 0 20%" }}
                key={col.key}
                onClick={() => toggleColumn(col.key)}
              >
                <Checkbox
                  state={visibleColumns.find((c) => c.key === col.key)!.visible}
                  setState={() => { }}
                />

                <LittleText>{col.label}</LittleText>
              </span>
            )
          )}
        </div>
      )}
      {/* Таблиця */}
      {paginatedData.length >= 1 ? (
        <>
          <div
            className="table-responsive"
            style={{
              display: "block",
              width: "100%",
              overflowX: "auto",
            }}
          >
            <table
              className="w-100"
              style={{
                tableLayout: "auto",
                whiteSpace: "nowrap",
              }}
            >
              <thead className="overflow-x-auto w-100">
                <tr>
                  {visibleColumns
                    .filter((col) => col.visible)
                    .map((col) => (
                      <th
                        key={col.key}
                        className={`text-left cursor-pointer select-none text-nowrap ${col.key === "isChecked" ? "pt-12" : ""
                          }`}
                        onClick={() => {
                          if (col.key === "isChecked") {
                            return;
                          }
                          handleSort(col.key);
                        }}
                      >
                        {col.key === "isChecked" ? (
                          <span
                            onClick={() => {
                              if (selectedRows.size === paginatedData.length) {
                                setSelectedRows(new Set());
                              } else {
                                setSelectedRows(
                                  new Set(
                                    paginatedData.map((item) => Number(item.id))
                                  )
                                );
                              }
                            }}
                          >
                            {" "}
                            <Checkbox
                              state={selectedRows.size === paginatedData.length}
                              setState={() => { }}
                            />
                          </span>
                        ) : (col.key.toLocaleLowerCase().includes("project") &&
                          isPage === TableTypeEnum.PROJECTS) ||
                          (col.key.toLocaleLowerCase().includes("selling") &&
                            isPage === ("selling" as TableTypeEnum)) ||
                          col.key.toLocaleLowerCase().includes("id") ||
                          col.key === "projectId" ||
                          col.key === "sellingId" ? null : (
                          <span className="d-flex align-items-center justify-content-start gap-2">
                            <LittleText>{col.label}</LittleText>
                            <img
                              src={
                                sortConfig?.key === col.key
                                  ? sortConfig.direction === "desc"
                                    ? tableArrowUp
                                    : tableArrowDown
                                  : tableArrowDown
                              }
                              alt="table-arrow"
                            />
                          </span>
                        )}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="overflow-x-auto w-100">
                {paginatedData.map((row, i) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {visibleColumns
                      .filter((col) => col.visible)
                      .map((col) => (
                        <td
                          key={col.key}
                          className={`text-left cursor-pointer select-none text-nowrap ${col.key === "isChecked" ? "pt-12" : ""
                            }`}
                        >
                          {col.key === "isChecked" ? (
                            <span onClick={() => {
                              if ('isDelete' in row) {
                                if (row.isDelete) toggleRow(row["id"]);
                                else
                                  toast((t) => (
                                    <CustomToast
                                      t={t}
                                      type={ToastTypeEnum.INFO}
                                      text={"У вас немає доступу на видалення."} />
                                  ))
                              }
                              else { toggleRow(row["id"]) }
                            }}>
                              <Checkbox
                                state={selectedRows.has(Number(row["id"]))}
                                setState={() => { }}
                              />
                            </span>
                          ) : col.key === "siteLink" &&
                            row["siteLink"] !== "" ? (
                            <a
                              href={row[col.key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: COLORS.blue,
                                textDecoration: "underline",
                                marginTop: "8px",
                              }}
                            >
                              <LittleText>{row[col.key]}</LittleText>
                            </a>
                          ) : (col.key === "usersEmails" ||
                            col.key === "contactEmail" ||
                            col.key.includes("responsible") ||
                            col.key === "createdBy" ||
                            col.key === "email" ||
                            col.key === "clientEmail" ||
                            col.key.includes("phone")) &&
                            row[col.key] ? (
                            <span className="d-flex align-items-center gap-1">
                              <LittleText>
                                {col.key === "usersEmails"
                                  ? row["usersEmails"]?.[0]?.email
                                  : row[col.key]}
                              </LittleText>
                              {col.key === "usersEmails" && (
                                <NotificationText>
                                  {" "}
                                  {row[col.key].length > 1 &&
                                    `+${row[col.key].length - 1}`}
                                </NotificationText>
                              )}
                              {col.key === "usersEmails" &&
                                row["usersEmails"].length === 0 ? (
                                "—"
                              ) : (
                                <CopyButton
                                  text={
                                    Array.isArray(row[col.key])
                                      ? row[col.key]
                                        .map((x: any) => x.email)
                                        .join(", ")
                                      : row[col.key]?.toString() ?? ""
                                  }
                                />
                              )}
                            </span>
                          ) : col.key === "projectId" ||
                            col.key === "sellingId" ? null : (col.key ===
                              "project" &&
                              row["project"] === "") ||
                              (col.key === "selling" &&
                                (row["selling"] === "" || !row["selling"])) ? (
                            "—"
                          ) : col.key === "project" || col.key === "selling" ? (
                            (col.key.toLocaleLowerCase().includes("project") &&
                              isPage === TableTypeEnum.PROJECTS) ||
                              (col.key.toLocaleLowerCase().includes("selling") &&
                                isPage === ("selling" as TableTypeEnum)) ||
                              col.key
                                .toLocaleLowerCase()
                                .includes("id") ? null : (
                              <LinkTableText
                                href={`/${col.key === TableTypeEnum.SELLING
                                  ? TableTypeEnum.SELLING
                                  : `${col.key}s`
                                  }/details/${row[`${col.key}Id`]}`}
                              >
                                {(row[col.key] ?? "")?.length > 30
                                  ? `${(row[col.key] ?? "").slice(0, 30)}...`
                                  : (row[col.key] ||
                                    Array(row[col.key]).length === 0) ??
                                  "—"}
                              </LinkTableText>
                            )
                          ) : (
                            <BodyItem
                              $bgColor={
                                col.key === "customerType"
                                  ? typeClientObj[
                                    row["customerType"] as TypeClientEnum
                                  ].bgColor
                                  : col.key === "priorityName"
                                    ? prioritiesObj[
                                      row["priorityName"] as PriorityEnum
                                    ].bgColor
                                    : col.key === "projectStageName" ||
                                      col.key === "stage"
                                      ? stagesObj[
                                        (row["projectStageName"] as StageEnum) ||
                                        (row["stage"] as StageEnum)
                                      ].bgColor
                                      : "transparent"
                              }
                              $txColor={
                                col.key === "customerType"
                                  ? typeClientObj[
                                    row["customerType"] as TypeClientEnum
                                  ].txColor
                                  : col.key === "priorityName"
                                    ? prioritiesObj[
                                      row["priorityName"] as PriorityEnum
                                    ].txColor
                                    : col.key === "projectStageName" ||
                                      col.key === "stage"
                                      ? stagesObj[
                                        (row["projectStageName"] as StageEnum) ||
                                        (row["stage"] as StageEnum)
                                      ].txColor
                                      : COLORS.secondary1
                              }
                              $itemType={
                                col.key === "priorityName" ||
                                col.key === "projectStageName" ||
                                col.key === "stage" ||
                                col.key === "customerType" ||
                                (col.key === "status" &&
                                  typeof row[col.key] === "boolean")
                              }
                              $isStatusItem={
                                col.key === "status" &&
                                typeof row[col.key] === "boolean"
                              }
                              $statusItemValue={row["status"]}
                              onClick={() => {
                                if (col.key.toLowerCase().includes("name")) {
                                  const tableName = getEnumKeyByName(
                                    table.name
                                  );
                                  const linkBody =
                                    tableName === TableTypeEnum.TEAM_USERS ? "users" :
                                      tableName ===
                                        TableTypeEnum.COMPANY_EMPLOYEES ||
                                        tableName ===
                                        TableTypeEnum.STATISTIC_CLIENTS
                                        ? TableTypeEnum.CLIENTS
                                        : tableName === TableTypeEnum.SELLING
                                          ? "selling"
                                          : tableName;
                                  if (tableName)
                                    navigate(
                                      `/${linkBody}${isFinanceTitle
                                        ? isFinanceTitle ===
                                          FinanceTypeTable.INCOME
                                          ? "/income"
                                          : "/expense"
                                        : ""
                                      }/details/${row["id"]}`
                                    );
                                }
                              }}
                            >
                              {/* {col.key === "status" &&
                                typeof row[col.key] === "boolean" && (
                                  <img
                                    src={
                                      row[col.key]
                                        ? trueStatusIcon
                                        : falseStatusIcon
                                    }
                                    alt="status-icon"
                                  />
                                )} */}
                              {(col.key === "description" &&
                                row["description"] !== "") ||
                                (col.key === "comment" &&
                                  row["comment"] !== "") ||
                                (col.key === "name" && row["name"] !== "") ||
                                (col.key === "project" && row["project"] !== "")
                                ? row[col.key].length > 30
                                  ? `${row[col.key].slice(0, 30)}...`
                                  : row[col.key]
                                : row[col.key] === "" || row[col.key] === null
                                  ? "—" : (getEnumKeyByName(table.name) === TableTypeEnum.STATISTIC_USER_ITEM && (col.key === "projectsCount" || col.key === "tasksCount" || col.key === "commentsCount")) ? (<span className="d-flex flex-row gap-2 align-items-center flex-wrap justify-content-start">
                                    <LittleText>{row[col.key]}</LittleText>
                                    {<NotificationText style={{ color: getChangePercentTableStatisticByMetric(row, col.key).includes("+") ? COLORS.main1 : getChangePercentTableStatisticByMetric(row, col.key).includes("-") ? COLORS.warning : COLORS.blue }}>{getChangePercentTableStatisticByMetric(row, col.key)}</NotificationText>}
                                  </span>)
                                    : col.key === "status" &&
                                      typeof row[col.key] === "boolean"
                                      ? row[col.key]
                                        ? "Завершено"
                                        : "Не виконано"
                                      : col.key === "createdAt" ||
                                        col.key === "deadline" ||
                                        col.key === "createdAt" ||
                                        col.key === "date"
                                        ? formatDate(row[col.key])
                                        : row[col.key]}
                            </BodyItem>
                          )}
                        </td>
                      ))}
                    {(isPage === TableTypeEnum.TEAM_USERS) && (
                      <td className="actions-cell">
                        <button
                          className="row-actions-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveRowId(prev => (prev === row.id ? null : row.id));
                            setIsActiveClickedRow(!isActiveClickedRow);
                          }}
                        >
                          <img src={dotsIcon} alt="dots-icon" />
                        </button>

                        {(isActiveClickedRow && activeRowId === row.id) && (
                          <div className="row-actions-dropdown" ref={actionMenuRef}>
                            <span onClick={() => navigate(`/settings/team/update/${row.id}`)}>
                              <img src={penIcon} alt="pen-icon" />
                              <NotificationText>Редагувати</NotificationText>
                            </span>
                            <span onClick={() =>
                              navigate(`/settings/team/delete/${row.id}`, {
                                state: { background: location },
                              })
                            }>
                              <img src={deleteDarkIcon} alt="delete-icon" />
                              <NotificationText>Видалити</NotificationText></span>
                          </div>
                        )}
                      </td>
                    )}

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 📄 Пагінація */}
          <div
            className="d-flex justify-content-between align-items-center"
            data-type="pagination-block"
          >
            <span className="d-flex align-items-center gap-1">
              <Dropdown
                type={DropdownTypeEnum.NUMBERS}
                checkedValue={String(pageSize)}
                setChecked={(val) => setPageSize(Number(val))}
                numberArray={pageSizeOptions}
              />
              <LittleText>елементів на сторінці</LittleText>
              <LittleText className="ps-2">
                {/* {(Number(page) - 1) * pageSize + 1}– */}
                {(Number(page) - 1) * pageSize + paginatedData.length} з{" "}
                {data.length}
              </LittleText>
            </span>
            <span className="d-flex align-items-center gap-1">
              <Dropdown
                type={DropdownTypeEnum.NUMBERS}
                checkedValue={page}
                setChecked={setPage}
                numberArray={[...Array(totalPages)].map((_, i) => i + 1)}
              />
              <LittleText>з {totalPages}</LittleText>
              <LittleText>сторінок</LittleText>{" "}
              <Button
                bgcolor={"transparent"}
                txtcolor={"transparent"}
                brcolor={"transparent"}
                width="10px"
                type="large"
                brradius="12px"
                disabled={Number.parseInt(page) === 1}
                onClick={() =>
                  setPage((p) => (Number.parseInt(p) - 1).toString())
                }
                className="disabled:opacity-50"
              >
                <img src={arrowLeft} alt="arrow-left-icon" />
              </Button>
              <Button
                bgcolor={"transparent"}
                txtcolor={"transparent"}
                brcolor={"transparent"}
                width="10px"
                type="large"
                brradius="12px"
                disabled={Number.parseInt(page) === totalPages}
                onClick={() =>
                  setPage((p) => (Number.parseInt(p) + 1).toString())
                }
                className="disabled:opacity-50"
              >
                <img src={arrowRight} alt="arrow-right" />
              </Button>
            </span>
          </div>
        </>
      ) : (
        <ErrorBlock>
          <LittleSubHeadline>Дані для цієї таблиці відсутні</LittleSubHeadline>
        </ErrorBlock>
      )
      }
    </TableBlock >
  );
}

const LinkTableText = styled.a`
  ${LittleText.componentStyle.rules.join("")}
  font-weight: 650px;
  color: ${COLORS.blue};
  ${linkText};
`;

export const ErrorBlock = styled.div`
  width: 100%;
  padding-top: 16px;
  color: ${COLORS.secondary1};
`;

export const TableBlock = styled.div`

    .actions-cell {
      /* padding: 0; */
      width: 40px;
      position: absolute;
      right: 5%;
      /* z-index: 1003; */
      /* text-align: right;
      position: relative; */
    }

    .row-actions-btn {
      opacity: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 5px 10px;
      border-radius: 10px;
      transition: opacity 0.15s ease;
      img {
        height: 12px;
      }

      &:active {
        opacity: 1;
      }

      &:hover, &:active {
        background-color: ${COLORS.gray};
      }
    }

    tr:hover .row-actions-btn {
      opacity: 1;
    }

    .row-actions-dropdown {
      position: absolute;
      right: 0;
      /* top: 12px; */
      background: ${COLORS.white};
      border-radius: 8px;
      ${boxShadow(COLORS["light-bg-color"])}
      z-index: 10;
      padding: 6px 0;

     span {
        display: flex;
        align-items: center;
        gap: 6px;
        width: 100%;
        padding: 6px 12px;
        background: transparent;
        ${NotificationText} {
          font-weight: 500;
        text-align: left;
        color: ${COLORS.secondary1};
      }
        border: none;
        
        cursor: pointer;

        img {
          width: 16px;
          height: 16px;
        }

        &:hover {
          background: ${COLORS["gray"]};
        }
      }
    }
  
  /* margin-top: 16px; */
  ::-webkit-scrollbar {
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background-color: ${COLORS.gray};
    border-radius: 100px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${COLORS["light-gray"]};
    border: 1px solid ${COLORS["bg-text-color"]};
    border-radius: 100px;
  }
  background-color: ${COLORS.white};
  ${boxShadow("rgba(9, 21, 64, 0.2)")};
  border-radius: 12px;
  border: none;
  padding: 16px;
  color: ${COLORS.secondary1};

  [data-type="panel-block"],
  th p {
    padding-left: 0;
  }

  ${InputStyle} {
    padding: 0;
    min-width: 250px;
    span {
      padding: 4px 12px;
      border: 1px solid ${COLORS.secondary1};
      background-color: transparent;
    }
    ${LabelText} {
      display: none;
    }
    input {
      ${LittleText.componentStyle.rules.join("")}
    }
  }

  [data-type="pagination-block"] {
    ${LittleText} {
      color: ${COLORS["bg-text-color"]};
    }
    padding: 0;
    padding-top: 12px;
    ${Button} {
      margin: 0 4px;
      padding: 0;
    }
  }

  thead {
    border-spacing: 0 8px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${COLORS.gray};
  }

  table {
    margin-top: 8px;
    th,
    td {
      cursor: pointer;
      padding: 8px 20px 8px 0;
    }
    th {
      padding-bottom: 16px;
    }
  }
  ${NotificationText} {
    color: ${COLORS.black1};
    font-size: 13px;
  }

  [data-type="panel-block"] {
    border-bottom: 1px solid ${COLORS.gray};
    padding-bottom: 8px;
    span {
      display: flex;
      gap: 6px;
      ${Button} {
        border-radius: 8px;
        padding: 6px 8px;
      }
    }
  }
`;

const BodyItem = styled(LittleText) <{
  $bgColor: string;
  $txColor: string;
  $itemType: boolean;
  $isStatusItem: boolean;
  $statusItemValue: boolean;
}>`
  background-color: ${({ $bgColor }) => $bgColor};
  color: ${({ $txColor }) => $txColor};
  text-align: ${({ $itemType }) => ($itemType ? "center" : "left")};
  width: 100%;
  padding: ${({ $itemType }) => ($itemType ? "4px 10px" : "")};
  border-radius: 8px;

  ${({ $isStatusItem, $itemType, $statusItemValue }) =>
    $isStatusItem &&
    $itemType &&
    css`
      ${statusBtn(
      $statusItemValue ? COLORS["light-green"] : COLORS["light-warning"],
      $statusItemValue ? COLORS.main1 : COLORS.warning,
      "row"
    )};
      justify-content: center;
      padding: 4px 12px;
      //gap: ${$statusItemValue ? "0px" : "10px"};
    `}
`;
